import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { AuthContext } from '@/contexts/auth-context'
import { clearAuthSession, getAuthSession, setAuthSession } from '@/services/auth'
import type { AdminUser } from '@/services/admin/types'

type AuthProviderProps = {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [token, setToken] = useState<string | null>(() => getAuthSession())
  const [user, setUser] = useState<AdminUser | null>(null)
  const [userLoading, setUserLoading] = useState(Boolean(getAuthSession()))
  const skipNextProfileFetch = useRef(false)

  const refreshUser = useCallback(async () => {
    const t = getAuthSession()
    if (!t) {
      setUser(null)
      setUserLoading(false)
      return
    }
    setUserLoading(true)
    try {
      const { fetchProfile } = await import('@/services/admin/profileApi')
      const me = await fetchProfile()
      setUser(me)
    } catch {
      setUser(null)
      clearAuthSession()
      setToken(null)
    } finally {
      setUserLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!token) {
      setUser(null)
      setUserLoading(false)
      return
    }
    if (skipNextProfileFetch.current) {
      skipNextProfileFetch.current = false
      return
    }
    void refreshUser()
  }, [token, refreshUser])

  const value = useMemo(
    () => ({
      token,
      user,
      isAuthenticated: Boolean(token),
      userLoading,
      signIn(nextToken: string, nextUser?: AdminUser) {
        setAuthSession(nextToken)
        setToken(nextToken)
        if (nextUser) {
          skipNextProfileFetch.current = true
          setUser(nextUser)
          setUserLoading(false)
        } else {
          setUserLoading(true)
        }
      },
      refreshUser,
      signOut() {
        clearAuthSession()
        setToken(null)
        setUser(null)
        setUserLoading(false)
      },
    }),
    [token, user, userLoading, refreshUser],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
